import { cn } from '@/lib/cn';
import { dayjs, formatCalendarDate } from '@/lib/datetime';
import { For, type JSX, Show, createEffect, createMemo, createSignal, onCleanup } from 'solid-js';
import { Portal } from 'solid-js/web';
import { Button } from './Button';
import { Icon } from './Icon';

const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const toIso = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const fromIso = (value: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

export interface DatePickerProps {
  /** ISO date (YYYY-MM-DD) or '' for unset. */
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  hint?: string;
  required?: boolean;
  min?: string;
  max?: string;
  class?: string;
}

/** life-memo-style date picker: Input-look trigger + portal calendar panel. */
export const DatePicker = (props: DatePickerProps) => {
  const [open, setOpen] = createSignal(false);
  const [anchor, setAnchor] = createSignal<DOMRect | null>(null);
  let triggerRef: HTMLButtonElement | undefined;
  let panelRef: HTMLDivElement | undefined;

  const selected = () => fromIso(props.value);

  const openPanel = () => {
    setAnchor(triggerRef?.getBoundingClientRect() ?? null);
    setOpen(true);
    queueMicrotask(() =>
      (
        panelRef?.querySelector<HTMLButtonElement>('[aria-pressed="true"]') ??
        panelRef?.querySelector<HTMLButtonElement>('button')
      )?.focus(),
    );
  };

  createEffect(() => {
    if (!open()) return;
    const update = () => setAnchor(triggerRef?.getBoundingClientRect() ?? null);
    const onOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef?.contains(t) || panelRef?.contains(t)) return;
      setOpen(false);
    };
    const onKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        triggerRef?.focus();
      }
    };
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('keydown', onKeydown);
    onCleanup(() => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('keydown', onKeydown);
    });
  });

  const panelStyle = (): JSX.CSSProperties => {
    const rect = anchor();
    if (!rect) return {};
    return {
      position: 'fixed',
      top: `${Math.max(8, Math.min(rect.bottom + 4, window.innerHeight - 360))}px`,
      left: `${Math.max(8, Math.min(rect.left, window.innerWidth - 296))}px`,
      'max-height': 'calc(100dvh - 16px)',
      'overflow-y': 'auto',
      'z-index': 60,
    };
  };

  const display = () => formatCalendarDate(props.value) || undefined;

  return (
    <div class={cn('flex flex-col gap-1 text-sm', props.class)}>
      <Show when={props.label}>
        <span
          class={cn(
            'font-medium text-neutral-700 dark:text-neutral-300',
            props.required && "after:ml-0.5 after:text-red-500 after:content-['*']",
          )}
        >
          {props.label}
        </span>
      </Show>
      <button
        ref={triggerRef}
        type="button"
        aria-label={props.label}
        aria-haspopup="dialog"
        aria-expanded={open()}
        onClick={() => (open() ? setOpen(false) : openPanel())}
        class={cn(
          'relative h-10 w-full rounded-lg border bg-white px-3 pr-8 text-left text-sm outline-none transition',
          'border-neutral-300 hover:border-neutral-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500',
          'dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100',
        )}
      >
        <span class={cn('block truncate', !selected() && 'text-neutral-500')}>
          {display() ?? props.placeholder ?? 'Select date…'}
        </span>
        <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5">
          <Icon name="calendar" class="text-neutral-400" />
        </span>
      </button>
      <Show when={props.hint}>
        <span class="text-xs text-neutral-500">{props.hint}</span>
      </Show>

      <Show when={open()}>
        <Portal>
          <div
            ref={panelRef}
            role="dialog"
            aria-label={props.label ?? 'Choose date'}
            style={panelStyle()}
            class="min-w-[280px] rounded-lg border border-neutral-200 bg-surface p-3 shadow-lg dark:border-neutral-700 dark:bg-surface-dark"
          >
            <Calendar
              value={props.value}
              min={props.min}
              max={props.max}
              onChange={(date) => {
                props.onChange(date);
                setOpen(false);
                triggerRef?.focus();
              }}
            />
            <Show when={props.value}>
              <div class="mt-2 flex justify-end border-t border-neutral-100 pt-2 dark:border-neutral-800">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    props.onChange('');
                    setOpen(false);
                    triggerRef?.focus();
                  }}
                >
                  Clear
                </Button>
              </div>
            </Show>
          </div>
        </Portal>
      </Show>
    </div>
  );
};

/** The existing calendar surface, shared by DatePicker and DateTimePicker. */
export const Calendar = (props: Pick<DatePickerProps, 'value' | 'onChange' | 'min' | 'max'>) => {
  const [viewDate, setViewDate] = createSignal(new Date());
  const selected = () => fromIso(props.value);
  createEffect(() => {
    let date = props.value || toIso(new Date());
    if (props.min && date < props.min) date = props.min;
    if (props.max && date > props.max) date = props.max;
    setViewDate(fromIso(date) ?? new Date());
  });
  const days = createMemo(() => {
    const year = viewDate().getFullYear();
    const month = viewDate().getMonth();
    const startDayOfWeek = new Date(year, month, 1).getDay();
    const list: { date: Date; inMonth: boolean }[] = [];
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      list.push({ date: new Date(year, month, -i), inMonth: false });
    }
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let i = 1; i <= daysInMonth; i++) {
      list.push({ date: new Date(year, month, i), inMonth: true });
    }
    while (list.length < 42) {
      const last = list[list.length - 1] as { date: Date };
      list.push({
        date: new Date(last.date.getFullYear(), last.date.getMonth(), last.date.getDate() + 1),
        inMonth: false,
      });
    }
    return list;
  });

  const isSameDay = (a: Date, b: Date | null) => b != null && a.toDateString() === b.toDateString();
  const navigateMonth = (delta: number) =>
    setViewDate((v) => new Date(v.getFullYear(), v.getMonth() + delta, 1));

  const outsideBounds = (date: Date) =>
    Boolean((props.min && toIso(date) < props.min) || (props.max && toIso(date) > props.max));

  return (
    <div class="min-w-[256px]">
      {' '}
      <div class="mb-3 flex items-center justify-between">
        <button
          type="button"
          aria-label="Previous month"
          onClick={() => navigateMonth(-1)}
          class="rounded p-1 transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800"
        >
          <Icon name="angle-small-left" />
        </button>
        <span class="font-medium">{dayjs(viewDate()).format('MMMM YYYY')}</span>
        <button
          type="button"
          aria-label="Next month"
          onClick={() => navigateMonth(1)}
          class="rounded p-1 transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800"
        >
          <Icon name="angle-small-left" class="rotate-180" />
        </button>
      </div>
      <div class="mb-1 grid grid-cols-7 gap-1">
        <For each={WEEK_DAYS}>
          {(day) => <div class="py-1 text-center text-xs font-medium text-neutral-500">{day}</div>}
        </For>
      </div>
      <div class="grid grid-cols-7 gap-1">
        <For each={days()}>
          {({ date, inMonth }) => (
            <button
              type="button"
              aria-label={dayjs(date).format('dddd, MMMM D, YYYY')}
              aria-pressed={isSameDay(date, selected())}
              disabled={outsideBounds(date)}
              onClick={() => props.onChange(toIso(date))}
              class={cn(
                'disabled:opacity-30 disabled:cursor-not-allowed flex h-8 w-8 items-center justify-center rounded-full text-sm transition-colors',
                inMonth
                  ? 'text-neutral-900 dark:text-neutral-100'
                  : 'text-neutral-300 dark:text-neutral-600',
                isSameDay(date, selected())
                  ? 'bg-brand-600 text-white'
                  : 'hover:bg-neutral-100 dark:hover:bg-neutral-800',
                isSameDay(date, new Date()) &&
                  !isSameDay(date, selected()) &&
                  'ring-1 ring-brand-600',
              )}
            >
              {date.getDate()}
            </button>
          )}
        </For>
      </div>
    </div>
  );
};
